import { FC } from 'react';
import {
  FormLifecycleState,
  IFillingProgress,
  IFormResultVersionDto,
  RouteProgressStateEnum,
} from 'src/services/api/api-client';
import Style from './form-helper.module.scss';

import { ReactComponent as CrossIcon16 } from 'src/assets/img/formState/cross_16.svg';
import { ReactComponent as DoubleCrossIcon16 } from 'src/assets/img/formState/double_cross_16.svg';
import { ReactComponent as DotIcon16 } from 'src/assets/img/formState/dot_16.svg';
import { ReactComponent as CheckIcon16 } from 'src/assets/img/formState/check_16.svg';
import { ReactComponent as DoubleCheckIcon16 } from 'src/assets/img/formState/double_check_16.svg';
import { ReactComponent as Revoked16 } from 'src/assets/img/formState/revoked_16.svg';

import { ReactComponent as EditIcon20 } from 'src/assets/img/common/edit_20.svg';
import { ReactComponent as CrossIcon20 } from 'src/assets/img/formState/cross_20.svg';
import { ReactComponent as ResetIcon20 } from 'src/assets/img/common/reset_20.svg';
import { ReactComponent as CheckIcon20 } from 'src/assets/img/formState/check_20.svg';
import { ReactComponent as DoubleCheckIcon20 } from 'src/assets/img/formState/double_check_20.svg';
import { ReactComponent as DoubleCrossIcon20 } from 'src/assets/img/formState/double_cross_20.svg';
import clsx from 'clsx';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';

export type FormStateType =
  | 'filled'
  | 'submitted'
  | 'submittedAndApproved'
  | 'skipped'
  | 'skippedAndApproved'
  | 'none'
  | 'recalled'
  | 'missed';

export const getFormState = (
  formResultId: number | undefined | null,
  formLifecycleState: FormLifecycleState | undefined | null,
  isSkipped: boolean | undefined | null,
  fillingProgress: IFillingProgress | null | undefined,
  stepState: RouteProgressStateEnum | null,
  studyHasEcrf: boolean,
): FormStateType => {
  if (studyHasEcrf) {
    const isPassed = stepState === RouteProgressStateEnum.Done || stepState === RouteProgressStateEnum.Missed;
    const isSubmitted = formLifecycleState === FormLifecycleState.Submitted;
    const isApproved = formLifecycleState === FormLifecycleState.Approved;
    const isRecalled = formLifecycleState === FormLifecycleState.ApproveRecalled;

    switch (true) {
      case isRecalled:
        return 'recalled';
      case isApproved && isSkipped:
        return 'skippedAndApproved';
      case isSubmitted && isSkipped:
        return 'skipped';
      case isApproved:
        return 'submittedAndApproved';
      case isSubmitted:
        return 'submitted';
      case !!formResultId:
        return 'none';
      case isPassed:
        return 'missed';
      default:
        return 'none';
    }
  } else {
    const isPassed = stepState === RouteProgressStateEnum.Done || stepState === RouteProgressStateEnum.Missed;

    switch (true) {
      case formResultId && fillingProgress && fillingProgress.filled === fillingProgress.total:
        return 'filled';
      case !!formResultId:
        return 'none';
      case isPassed:
        return 'missed';
      default:
        return 'none';
    }
  }
};

export const getFormStateMultiInstance = (
  studyHasEcrf: boolean,
  aggregatedLifecycleState: FormLifecycleState | null,
): FormStateType | undefined => {
  if (!studyHasEcrf || aggregatedLifecycleState === null) return undefined;

  switch (true) {
    case aggregatedLifecycleState === FormLifecycleState.ApproveRecalled:
      return 'recalled';
    case aggregatedLifecycleState === FormLifecycleState.Approved:
      return 'submittedAndApproved';
    case aggregatedLifecycleState === FormLifecycleState.Draft:
      return 'none';
    default:
      return 'filled';
  }
};

export const FormStateIcon: FC<{
  state: FormStateType | undefined;
  variant: 'PatientCard' | 'FormOverview';
  className?: string;
}> = ({ state, variant, className }) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.GeneralInfo.Records');
  if (!state) return <></>;

  const TooltipText = {
    submitted: t('FormStateTooltips.submitted'),
    submittedAndApproved: t('FormStateTooltips.submittedAndApproved'),
    filled: t('FormStateTooltips.filled'),
    skippedAndApproved: t('FormStateTooltips.missedAndApproved'),
    skipped: t('FormStateTooltips.skipped'),
    missed: t('FormStateTooltips.missed'),
    none: t('FormStateTooltips.none'),
    recalled: t('FormStateTooltips.recalled'),
  }[state];

  const CardIcon = {
    submittedAndApproved: DoubleCheckIcon16,
    submitted: CheckIcon16,
    filled: CheckIcon16,
    skippedAndApproved: DoubleCrossIcon16,
    skipped: CrossIcon16,
    missed: CrossIcon16,
    none: DotIcon16,
    recalled: Revoked16,
  }[state];

  const OverviewIcon = {
    submittedAndApproved: DoubleCheckIcon20,
    submitted: CheckIcon20,
    filled: CheckIcon20,
    skippedAndApproved: DoubleCrossIcon20,
    skipped: CrossIcon20,
    missed: CrossIcon20,
    none: EditIcon20,
    recalled: ResetIcon20,
  }[state];

  return variant === 'PatientCard' ? (
    <Tooltip text={TooltipText} hostStyles={Style.formStateTooltip}>
      <CardIcon className={clsx(Style.icon, className)} />
    </Tooltip>
  ) : (
    <OverviewIcon className={clsx(Style.icon, className)} />
  );
};

export const getFormStateColor = (state: FormStateType | undefined, variant: 'PatientCard' | 'FormOverview') => {
  if (!state) return undefined;

  return {
    submittedAndApproved: Style.green,
    submitted: Style.grey,
    filled: Style.grey,
    skippedAndApproved: Style.green,
    skipped: Style.grey,
    missed: Style.grey,
    none: variant === 'PatientCard' ? Style.black : Style.grey,
    recalled: Style.red,
  }[state];
};

export const isFirstResultVersion = (lastFormVersion: IFormResultVersionDto) =>
  lastFormVersion.versionNumber === 1 && lastFormVersion.minorVersionNumber === 0;
